import React, { useState, useEffect } from "react";
import { MPSharedHeader, MPSharedTable, MPSharedDeleteModal } from "../Shared";
import {
  useGetCustomFieldsQuery,
  useDeleteCustomFieldMutation,
} from "../services";
import { useSearchQuery, usePagination } from "../../../hooks";
import { CustomFieldForm } from "./Form";
import { getLocalizedString } from "../../../shared/translation";

export const CustomFieldsSection = () => {
  const { page, setPage, perPage, pagination } = usePagination();
  const [assetTypeFilter, setAssetTypeFilter] = useState("");

  const { setSimpleSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_cont",
  });

  // Build query params - add asset_type filter if selected
  // Note: API uses 0-based pagination, usePagination returns 1-based page
  const queryParams = {
    page: page - 1,
    per_page: perPage,
    order_by: "created_at",
    order_dir: "desc",
    q,
  };

  if (assetTypeFilter) {
    queryParams.asset_type = assetTypeFilter;
  }

  const {
    data = { data: [], total_count: 0 },
    isFetching,
    isSuccess,
    error,
  } = useGetCustomFieldsQuery(queryParams);

  const [deleteCustomField, { isSuccess: deleteSuccess, reset: resetDeleteCustomField }] =
    useDeleteCustomFieldMutation();

  const [idToEdit, setIdToEdit] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  const createNewHandler = () => {
    setShowForm(true);
    setIdToEdit(null);
  };

  const editClickHandler = (datum) => {
    setShowForm(true);
    setIdToEdit(datum.id);
  };

  const deleteClickHandler = (datum) => {
    setShowDeleteWarning(true);
    setIdToDelete(datum.id);
  };

  const getDeleteItemName = (id) => {
    let name = "";
    const index = data?.data?.findIndex((datum) => datum.id === id);

    if (index > -1) {
      name = data?.data[index].label || data?.data[index].name;
    }
    return name;
  };

  const headers = [
    { label: getLocalizedString("name", "Name"), key: "name", className: "field-name" },
    { label: getLocalizedString("label", "Label"), key: "label", className: "label" },
    { label: getLocalizedString("field_type", "Field Type"), key: "field_type", className: "field-type" },
  ];

  useEffect(() => {
    if (deleteSuccess) {
      setShowDeleteWarning(false);
      resetDeleteCustomField();
    }
    return () => resetDeleteCustomField();
  }, [deleteSuccess]);

  // Reset page when asset type filter changes
  useEffect(() => {
    setPage(1);
  }, [assetTypeFilter]);

  return (
    <>
      <article className="main-container">
        <MPSharedHeader
          createNewHandler={createNewHandler}
          groupName="configurations"
          resourceName="Custom Field Configs"
          heading={getLocalizedString("configurations", "Configurations")}
          handleSearchKey={(value) => setSimpleSearch(value)}
          simpleSearchPlaceHolder={getLocalizedString("search_by_name_or_label", "Search by name or label")}
          filterText={assetTypeFilter ? `${getLocalizedString("asset_type", "Asset Type")}: ${assetTypeFilter}` : ""}
          skipCanCreate={true}
        />

        <MPSharedTable
          resourceName="Custom Field Configs"
          isLoading={isFetching}
          error={error}
          isSuccess={isSuccess}
          data={data.data}
          headers={headers}
          pagination={{ ...pagination, count: data.total_count }}
          onEdit={editClickHandler}
          onDelete={deleteClickHandler}
          className="custom-fields"
          skipClientId
          skipCanEdit
          skipCanDelete
        />
      </article>

      {showForm && (
        <CustomFieldForm
          idToEdit={idToEdit}
          closeForm={() => setShowForm(false)}
        />
      )}

      <MPSharedDeleteModal
        show={showDeleteWarning}
        resourceName={getLocalizedString("custom_field", "custom field")}
        getDeleteItemName={() => getDeleteItemName(idToDelete)}
        onHide={() => setShowDeleteWarning(false)}
        onDelete={() => deleteCustomField({ id: idToDelete })}
      />
    </>
  );
};
